/**
 * Deal Value Display Component
 *
 * Shows the deal value for a customer with editing capability and OpenSolar sync status
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { PoundSterling, Edit2, Check, X, Lock, Unlock } from 'lucide-react';

interface DealValueDisplayProps {
  customerId: number;
  dealValue: number | null;
  additionalData?: any;
  isCompact?: boolean;
}

export function DealValueDisplay({
  customerId,
  dealValue,
  additionalData,
  isCompact = false,
}: DealValueDisplayProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(dealValue ? (dealValue / 100).toString() : '');

  // Check if this is a manual override or OpenSolar synced value
  const isManualOverride = additionalData?.manualDealValue === true;
  const hasOpenSolarSync = additionalData?.openSolarProjectId;
  const lastSync = additionalData?.lastOpenSolarSync;

  // Update customer deal value
  const updateDealValueMutation = useMutation({
    mutationFn: async (updates: { dealValue: number | null; manualOverride: boolean }) => {
      const response = await fetch(`/api/customers/${customerId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dealValue: updates.dealValue,
          additionalData: {
            ...additionalData,
            manualDealValue: updates.manualOverride,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update deal value');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate both customers and pipeline data to ensure consistency across views
      queryClient.invalidateQueries({ queryKey: ['/api/customers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/pipeline-stages/data'] });
      toast({
        title: 'Deal value updated',
        description: 'The deal value has been updated successfully.',
      });
      setIsEditing(false);
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update deal value. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const handleSave = () => {
    if (!editValue) {
      updateDealValueMutation.mutate({
        dealValue: null,
        manualOverride: true,
      });
      return;
    }

    // More precise decimal to pence conversion to avoid floating point errors
    const cleanValue = editValue.replace(/[^0-9.]/g, '');
    const parts = cleanValue.split('.');
    const pounds = parseInt(parts[0] || '0');
    const pence = parseInt((parts[1] || '00').padEnd(2, '0').slice(0, 2));
    const numericValue = pounds * 100 + pence;

    updateDealValueMutation.mutate({
      dealValue: numericValue,
      manualOverride: true,
    });
  };

  const handleCancel = () => {
    setEditValue(dealValue ? (dealValue / 100).toString() : '');
    setIsEditing(false);
  };

  const toggleManualOverride = () => {
    updateDealValueMutation.mutate({
      dealValue: dealValue,
      manualOverride: !isManualOverride,
    });
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return '£0';
    return `£${(value / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (isCompact) {
    return (
      <div className="flex items-center gap-2">
        <PoundSterling className="h-4 w-4 text-emerald-600" />
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {formatCurrency(dealValue)}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Deal Value Display/Edit */}
      <div className="flex items-center gap-2">
        <PoundSterling className="h-4 w-4 text-emerald-600" />
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              placeholder="0.00"
              className="w-24 h-8"
              step="0.01"
              min="0"
            />
            <Button size="sm" onClick={handleSave} disabled={updateDealValueMutation.isPending}>
              <Check className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="outline" onClick={handleCancel}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {formatCurrency(dealValue)}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsEditing(true)}
              className="h-6 w-6 p-0"
            >
              <Edit2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Status Badges and Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Manual Override Badge */}
        <Button size="sm" variant="outline" onClick={toggleManualOverride} className="h-6 text-xs">
          {isManualOverride ? (
            <>
              <Lock className="h-3 w-3 mr-1" />
              Manual
            </>
          ) : (
            <>
              <Unlock className="h-3 w-3 mr-1" />
              Auto
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
